import { getGroupMetadataFromEvent } from '@/lib/event-metadata'
import { toNote, toRelay } from '@/lib/link'
import { cn } from '@/lib/utils'
import { getSeenEventRelayUrls } from '@/services/client.service'
import RelayIcon from '../RelayIcon'
import { SecondaryPageLink } from '@/PageManager'
import { useFetchRelayInfo } from '@/hooks/useFetchRelayInfo'
import { Event } from '@nostr/tools/wasm'
import { pool } from '@nostr/gadgets/global'
import * as kinds from '@nostr/tools/kinds'
import { useEffect, useMemo, useState } from 'react'

export default function Nip29EventHeader({
  event,
  label,
  className
}: {
  event: Event
  label: string
  className?: string
}) {
  const groupId = event.tags.find(([name]) => name === 'h')?.[1]
  const relay = getSeenEventRelayUrls(event.id, event)[0]
  const { relayInfo } = useFetchRelayInfo(relay || '')
  const [metadataEvent, setMetadataEvent] = useState<Event>()

  useEffect(() => {
    if (!groupId || !relay) {
      setMetadataEvent(undefined)
      return
    }

    let cancelled = false
    setMetadataEvent(undefined)

    pool
      .querySync([relay], { kinds: [kinds.GroupMetadata], '#d': [groupId], limit: 1 })
      .then((events) => {
        if (!cancelled) {
          setMetadataEvent(
            [...events]
              .filter((candidate) => candidate.kind === kinds.GroupMetadata)
              .sort((a, b) => b.created_at - a.created_at)[0]
          )
        }
      })
      .catch(() => {
        if (!cancelled) setMetadataEvent(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [groupId, relay])

  const metadata = useMemo(
    () => (metadataEvent ? getGroupMetadataFromEvent(metadataEvent) : undefined),
    [metadataEvent]
  )

  if (!metadata || !metadataEvent || !groupId || !relay) {
    return <div className={cn('text-sm text-muted-foreground font-medium', className)}>{label}</div>
  }

  const picture = metadataEvent?.tags.find(([name]) => name === 'picture' || name === 'icon')?.[1]
  const groupPointer = {
    pubkey: metadataEvent.pubkey,
    kind: kinds.GroupMetadata,
    identifier: groupId,
    relays: [relay]
  }

  return (
    <div
      className={cn('flex items-center gap-1 text-sm text-muted-foreground font-medium', className)}
    >
      {picture && <img src={picture} alt="" className="h-5 w-5 rounded object-cover" />}
      <span>{label} in</span>
      <SecondaryPageLink
        to={toNote(groupPointer)}
        className="truncate text-foreground hover:underline"
      >
        {metadata.name}
      </SecondaryPageLink>
      <span>at</span>
      <SecondaryPageLink
        to={toRelay(relay)}
        className="flex min-w-0 items-center gap-1 hover:underline"
      >
        <RelayIcon url={relay} className="h-4 w-4 shrink-0" iconSize={12} />
        <span className="truncate">{relayInfo.urlSimple}</span>
      </SecondaryPageLink>
    </div>
  )
}
